/**
 * The app-shell header's round icon control — the "dot" — as one recipe.
 *
 * The share action, the account avatar, and the phone sign-in link are the
 * same object doing different jobs, and they had drifted apart (36px circle,
 * 32px circle, 32px rounded SQUARE) inches from each other in one 56px bar.
 * One geometry here is what keeps them reading as a family.
 *
 * 36px — the share control's own original size — and deliberately NOT
 * `--tap-target`: sizing the header dots up to the editor's 44px-on-glass
 * token was tried and reverted on review; they read as oversized next to
 * the bar's text controls. The editor's floating dots over the 3D view
 * (room/IconButton.tsx) DO carry the token — those are primary touch
 * controls on the scene. The two families are different sizes on purpose.
 *
 * These consume `--app-*` / `--rt-*`, the app-shell palette: the header
 * deliberately rides that rather than the editor's `--ed-*` theme so landing
 * → dashboard → editor read as one continuous bar (see TopBar.tsx's header
 * comment). `room/IconButton.tsx` is this same shape in the editor theme;
 * the two are siblings, not duplicates.
 *
 * Not `ui/Button.tsx`: that is the ACTION ladder — a pill with a label, four
 * variants. An icon-only circle is a different species, which is why the
 * editor already had `IconButton` for it rather than a Button variant.
 */

/** Geometry only — for a dot that paints its own fill (the avatar). */
export const CHROME_DOT_BASE =
  'inline-flex h-9 w-9 shrink-0 items-center '
  + 'justify-center rounded-full transition-colors';

/** The default outline treatment, for a dot that is an action. Its glyph
 *  rests at ink-dim, not faint: faint is the disabled tier, and an enabled
 *  share or undo that looks disabled is a control people can't find. The
 *  real disabled state still reads as one — it drops to half opacity. */
export const CHROME_DOT =
  `${CHROME_DOT_BASE} border border-[var(--app-border-bright)] text-[var(--rt-ink-dim)] `
  + 'hover:bg-[var(--app-hover)] hover:text-[var(--rt-ink)] '
  + 'disabled:cursor-not-allowed disabled:opacity-40';
